// Mirror of data-types/SubscriptionTypes Care+ matrix shapes (the client does not import
// data-types). Kept in sync with GET /subscription-plans/:mention/:country/:language.

/** HL-11357: per-fee split of a Care+ plan's monthly charge. */
export interface CarePlusPlanFeeBreakdown {
  treatment_fee: number;
  service_fee: number;
  shipping_fee: number;
}

export type PlanBonusScope = "first_order" | "all_orders";

/** HL-11357: the plan's bonus as it lands on the fees, already capped server-side. */
export interface CarePlusPlanBonus {
  bonus_id: number;
  /** 'first_order' → the discount is a welcome offer; 'all_orders' → it never expires. */
  applies_to: PlanBonusScope;
  discount: number;
  discount_breakdown: Partial<CarePlusPlanFeeBreakdown>;
  /** Fees the bonus covers in full — rendered as the "Free services" rows. */
  fully_covered_fees: (keyof CarePlusPlanFeeBreakdown)[];
}

export interface CarePlusPlanDuration {
  subscription_plan_id: number;
  period_months: number;
  plan_fee: number;
  monthly_equivalent: number;
  total_for_period: number;
  discount: number;
  savings_per_month: number;
  savings_for_period: number;
  fee_breakdown: CarePlusPlanFeeBreakdown;
  /** Monthly fees before the plan bonus. */
  total_fee: number;
  /** Monthly fees after the plan bonus — what is actually billed. */
  charged_fee: number;
  bonus: CarePlusPlanBonus | null;
}

export interface CarePlusPlanTier {
  tier: string;
  title: string;
  durations: CarePlusPlanDuration[];
}

/** The plan tiers Choose Plan renders as cards. Anything else in the matrix is not shown. */
export const DISPLAYED_PLAN_TIERS = ["basic", "pro", "max"];

/**
 * The tiers the page will actually render, in the order it renders them.
 *
 * Order is the server's, not the order of DISPLAYED_PLAN_TIERS — the cards are laid out in
 * matrix order, so `displayedPlanTiers(tiers)[0]` IS the first card the customer sees. Shared so
 * the card list and the hook's pre-selection cannot drift apart.
 */
export const displayedPlanTiers = (tiers: CarePlusPlanTier[]): CarePlusPlanTier[] =>
  tiers.filter((tier) => DISPLAYED_PLAN_TIERS.includes(tier.tier));

/** HL-11357: the selected medication's own price components. */
export interface CarePlusProductFees {
  official_price: number;
  treatment_fee: number;
  service_fee: number;
  shipping_fee: number;
  /** Prescription-only price; differs from treatment_fee only where the category opts in. */
  prescription_only_treatment_fee: number;
}

export interface CarePlusOneTimeBonus {
  bonus_id: number;
  discount: number;
  discount_breakdown: Partial<CarePlusPlanFeeBreakdown>;
}

export interface CarePlusPlanMatrix {
  tiers: CarePlusPlanTier[];
  one_time_bonus: CarePlusOneTimeBonus | null;
  /** False when the product's catalog is subscription-only — hides the "One Time order" option. */
  allow_one_time_premium: boolean;
  /**
   * HL-11489: true when the product's catalog marks it a tablet product, which adds the base
   * card's pack-size relabel and second-month split. Optional — absent means the standing copy.
   * Not a pricing switch: every card prices the medication in with the services.
   */
  show_medication_in_plan_price?: boolean;
  product_fees: CarePlusProductFees;
  /** HL-12001: raw `choose_plan_order_ab_test_weights` variable (JSON per arm). Absent on old payloads. */
  choose_plan_order_ab_test_weights?: unknown;
  service_fee_wl_test?: "A" | "B";
}

export interface ChoosePlanSelection {
  tier: CarePlusPlanTier | null;
  duration: CarePlusPlanDuration | null;
}
